import React, { useState, useEffect } from "react";
import {
  FaCog,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUser,
  FaHistory,
  FaStar,
  FaExchangeAlt,
  FaChartLine,
  FaBars,
  FaTimes,
  FaBuilding,
  FaSignOutAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import SportsManagement from "./SportsManagement";
import BookingRequests from "./BookingRequests";
import SlotManagement from "./SlotManagement";
import FinanceManagement from "./FinanceManagement";
import ProfileManagement from "./ProfileManagement";
import BookingHistory from "./BookingHistory";
import Ratings from "./Ratings";
import OwnershipTransfer from "./OwnershipTransfer";
import { useArenaAuth } from "../../context/ArenaAuthContext";
import ConfirmationModal from "../../common/ConfirmationModal";

const ArenaDashboard = () => {
  const [arenaData, setArenaData] = useState(null);
  const [activeTab, setActiveTab] = useState("sports");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInstituteArena, setIsInstituteArena] = useState(false);
  const navigate = useNavigate();
  const { arenaUser, loading, logoutArena } = useArenaAuth();

  useEffect(() => {
    if (arenaUser) {
      setArenaData({ name: arenaUser.name, location: arenaUser.location });
    }
  }, [arenaUser]);

  useEffect(() => {
    const checkInstituteStatus = async () => {
      if (!arenaUser) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/check-institute-status`,
          { withCredentials: true }
        );
        setIsInstituteArena(response.data.isInstituteArena);
      } catch (error) {
        console.error("Error checking institute status:", error);
        // Default to false (regular arena) if API fails
        setIsInstituteArena(false);
      }
    };

    checkInstituteStatus();
  }, [arenaUser]);

  useEffect(() => {
    if (!loading) {
      if (!arenaUser) {
        alert("Login First as Arena");
        navigate("/LoginArena", { replace: true });
      }
    }
  }, [arenaUser, loading, navigate]);

  const renderContent = () => {
    // For institute arenas, restrict to available tabs
    if (
      isInstituteArena &&
      !["sports", "slots", "profile"].includes(activeTab)
    ) {
      setActiveTab("sports");
      return <SportsManagement />;
    }

    switch (activeTab) {
      case "sports":
        return <SportsManagement />;
      case "bookings":
        return <BookingRequests />;
      case "slots":
        return <SlotManagement />;
      case "finances":
        return <FinanceManagement />;
      case "profile":
        return <ProfileManagement />;
      case "history":
        return <BookingHistory />;
      case "ratings":
        return <Ratings />;
      case "ownership":
        return <OwnershipTransfer />;
      default:
        return <SportsManagement />;
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  };

  const handleERPAccess = () => {
    setIsModalOpen(true);
  };

  const renderSidebarButtons = () => {
    const buttons = [];

    // Always show Sports & Facilities
    buttons.push(
      <button
        key="sports"
        onClick={() => handleTabClick("sports")}
        className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
          activeTab === "sports"
            ? "bg-[#0071bc] text-white shadow-lg"
            : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
        }`}
      >
        <FaCog className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <span className="truncate">Sports & Facilities</span>
      </button>
    );

    // Show Booking Requests only for non-institute arenas
    if (!isInstituteArena) {
      buttons.push(
        <button
          key="bookings"
          onClick={() => handleTabClick("bookings")}
          className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
            activeTab === "bookings"
              ? "bg-[#0071bc] text-white shadow-lg"
              : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
          }`}
        >
          <FaCalendarAlt className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="truncate">Booking Requests</span>
        </button>
      );
    }

    // Always show Slot Management
    buttons.push(
      <button
        key="slots"
        onClick={() => handleTabClick("slots")}
        className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
          activeTab === "slots"
            ? "bg-[#0071bc] text-white shadow-lg"
            : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
        }`}
      >
        <FaCalendarAlt className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <span className="truncate">Slot Management</span>
      </button>
    );

    // Show Finances only for non-institute arenas
    if (!isInstituteArena) {
      buttons.push(
        <button
          key="finances"
          onClick={() => handleTabClick("finances")}
          className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
            activeTab === "finances"
              ? "bg-[#0071bc] text-white shadow-lg"
              : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
          }`}
        >
          <FaMoneyBillWave className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="truncate">Finances</span>
        </button>
      );
    }

    // Always show Profile
    buttons.push(
      <button
        key="profile"
        onClick={() => handleTabClick("profile")}
        className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
          activeTab === "profile"
            ? "bg-[#0071bc] text-white shadow-lg"
            : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
        }`}
      >
        <FaUser className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <span className="truncate">Profile</span>
      </button>
    );

    // Show Booking History only for non-institute arenas
    if (!isInstituteArena) {
      buttons.push(
        <button
          key="history"
          onClick={() => handleTabClick("history")}
          className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
            activeTab === "history"
              ? "bg-[#0071bc] text-white shadow-lg"
              : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
          }`}
        >
          <FaHistory className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="truncate">Booking History</span>
        </button>
      );
    }

    // Show Ratings only for non-institute arenas
    if (!isInstituteArena) {
      buttons.push(
        <button
          key="ratings"
          onClick={() => handleTabClick("ratings")}
          className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
            activeTab === "ratings"
              ? "bg-[#0071bc] text-white shadow-lg"
              : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
          }`}
        >
          <FaStar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="truncate">Ratings</span>
        </button>
      );
    }

    // Show Transfer Ownership only for non-institute arenas
    if (!isInstituteArena) {
      buttons.push(
        <button
          key="ownership"
          onClick={() => handleTabClick("ownership")}
          className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${
            activeTab === "ownership"
              ? "bg-[#0071bc] text-white shadow-lg"
              : "text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
          }`}
        >
          <FaExchangeAlt className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="truncate">Transfer Ownership</span>
        </button>
      );
    }

    // Show Asset Management (ERP) only for non-institute arenas
    if (!isInstituteArena) {
      buttons.push(
        <button
          key="erp"
          onClick={handleERPAccess}
          className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-sm sm:text-base text-gray-700 hover:bg-green-50 hover:text-green-600 border border-green-200 hover:border-green-300"
        >
          <FaChartLine className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="truncate">Asset Management</span>
        </button>
      );
    }

    return buttons;
  };

  const handleConfirmERP = async () => {
    setIsLoading(true);
    try {
      // Login to business ERP system (creates business reference if needed and sets JWT)
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/business-login`,
        {
          external_business_id: arenaUser?.arenaId,
          business_type: "arena",
        },
        { withCredentials: true }
      );

      // Navigate to asset dashboard (JWT is set in cookie)
      navigate("/assets-dashboard?type=arena");
    } catch (error) {
      console.error("Error accessing ERP system:", error);
      alert("Failed to access ERP system. Please try again.");
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutArena();
      toast.success("Logged out successfully!");
      navigate("/user-dashboard");
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Error during logout");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4 md:py-6">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-1 min-w-0">
              <img src="/logo.png" alt="Dsportshub Logo" className="h-8 sm:h-10 md:h-12 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1 className="text-sm sm:text-lg md:text-2xl font-bold text-gray-900 flex items-center gap-1 sm:gap-2 truncate">
                  <FaBuilding className="text-[#0071bc] flex-shrink-0 text-xs sm:text-sm md:text-base" />
                  <span className="truncate">Arena Dashboard</span>
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm truncate">
                  {arenaData?.name || "Arena Dashboard"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
              <button
                onClick={toggleSidebar}
                className="lg:hidden bg-[#0071bc] text-white p-2 rounded-lg sm:rounded-xl hover:bg-[#005a94] transition-colors"
              >
                {isSidebarOpen ? <FaTimes /> : <FaBars />}
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium hover:bg-red-700 transition-all duration-300 flex items-center gap-1 sm:gap-2 shadow-lg hover:shadow-xl"
              >
                <FaSignOutAlt className="text-xs sm:text-sm" />
                <span className="hidden xs:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex max-w-7xl mx-auto">
        {/* Sidebar */}
        <nav
          className={`bg-white shadow-xl border-r border-gray-200 transition-all duration-300 ${
            isSidebarOpen ? "w-64" : "w-64 hidden lg:block"
          } ${isSidebarOpen ? "fixed inset-y-0 left-0 z-50 lg:relative mt-0" : ""}`}
        >
          <div className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
              Navigation
            </h3>
            <div className="space-y-1 sm:space-y-2">{renderSidebarButtons()}</div>
          </div>
        </nav>

        {/* Overlay for mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-2 sm:p-4 md:p-6 w-full overflow-x-hidden">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 min-h-[400px] sm:min-h-[500px] md:min-h-[600px]">
            {renderContent()}
          </div>
        </main>
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        title="Access ERP System"
        message="Are you sure you want to access the Asset Management ERP system? This will log you into the business system."
        onConfirm={handleConfirmERP}
        onCancel={() => setIsModalOpen(false)}
        confirmText="Access ERP"
        cancelText="Cancel"
        isLoading={isLoading}
      />
    </div>
  );
};

export default ArenaDashboard;
